import {APP_INITIALIZER, enableProdMode, ErrorHandler, importProvidersFrom, provideZoneChangeDetection,} from '@angular/core';

import {version} from './app/app.module';
import {environment} from './environments/environment';
import * as Sentry from '@sentry/angular';
import {browserTracingIntegration, createErrorHandler, replayCanvasIntegration, replayIntegration, TraceService} from '@sentry/angular';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi,} from '@angular/common/http';
import {EditorModule, TINYMCE_SCRIPT_SRC} from '@tinymce/tinymce-angular';
import {BASE_PATH, UtishaBackendConfiguration} from '@api/utisha-backend';
import {AwsInitializer} from './app/aws/AwsInitializer';
import {UnauthorizedInterceptor} from './app/interceptors/unauthorized-interceptor';
import {HttpXsrfInterceptor} from './app/interceptors/xsrf-interceptor';
import {ZonedDatePipe} from './app/pipes/zoned-date-pipe';
import {DatePeriodPipe} from './app/pipes/date-period-pipe';
import {TimeDiffPipe} from './app/pipes/time-diff-pipe';
import {HtmlDocTypePipe} from './app/pipes/html-doc-type-pipe';
import {FileSizePipe} from './app/pipes/filesize-pipe';
import {VideoStatePipe} from './app/pipes/video-state-pipe';
import {LanguagePipe} from './app/pipes/language-pipe';
import {MissingTranslationHandler, TranslateModule, TranslateService,} from '@ngx-translate/core';
import {firstValueFrom} from 'rxjs';
import {UtishaMissingTranslationHandler} from './app/services/translation-missing-handler';
import {CookieService} from './app/services/cookie.service';
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';
import {SafeHtmlPipe} from './app/pipes/safeHtmlPipe';
import {ReversePipe} from './app/pipes/reverse-pipe';
import {SessionProviderFactory} from './app/events/session/SessionProviderFactory';
import {StreamSessionProvider} from './app/events/session/stream/StreamSessionProvider';
import {en_US, NZ_I18N} from 'ng-zorro-antd/i18n';
import {Router} from '@angular/router';
import {bootstrapApplication, BrowserModule} from '@angular/platform-browser';
import {CommonModule} from '@angular/common';
import {AppRoutingModule} from './app/app-routing.module';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {provideAnimations} from '@angular/platform-browser/animations';
import {NzPopoverModule} from 'ng-zorro-antd/popover';
import {NgScrollbarModule} from 'ngx-scrollbar';
import {NgcCookieConsentConfig, NgcCookieConsentModule,} from 'ngx-cookieconsent';
import {ServiceWorkerModule} from '@angular/service-worker';
import {AppComponent} from './app/app.component';
import {LastActiveInterceptor} from './app/interceptors/last-active-interceptor';
import {TenantInterceptor} from './app/interceptors/tenant-interceptor';

const cookieConfig: NgcCookieConsentConfig = {
  cookie: {
    domain: window.location.hostname,
  },
  palette: {
    popup: {
      background: '#f8f5f5',
      text: '#000000',
      link: '#ffffff',
    },
    button: {
      background: '#00639a',
      text: '#ffffff',
      border: 'transparent',
    },
  },
  theme: 'classic',
  type: 'opt-out',
  revokable: false,
  dismissOnScroll: true,
  revokeBtn: '<span></span>',
  blacklistPage: ['/timetable', /\/videos\/fullscreen\/.*/] as string[],
};

console.log(`Environment: ${environment.env}`);

if (environment.production) {
  enableProdMode();
}

const sentryIntegrations = [
  browserTracingIntegration(),
  replayIntegration(),
  replayCanvasIntegration(),
];

if (environment.sentry.captureCanvas) {
  console.log('Sentry canvas capture enabled');
  sentryIntegrations.push(replayCanvasIntegration());
}

if (environment.production) {
  Sentry.init({
    dsn: 'https://48be92ad51f64cd7868f248ffc482061@o4505171118194688.ingest.sentry.io/4505171119964160',
    integrations: sentryIntegrations,
    // Performance Monitoring
    tracesSampleRate: environment.production ? 0.1 : 1.0, // Capture 100% of the transactions, reduce in production!
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
    environment: environment.env,
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      BrowserModule,
      CommonModule,
      AppRoutingModule,
      FormsModule,
      ReactiveFormsModule,
      NzPopoverModule,
      EditorModule,
      NgScrollbarModule,
      NgcCookieConsentModule.forRoot(cookieConfig),
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: environment.serviceWorker,
        // Register the ServiceWorker as soon as the app is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: 'registerWhenStable:30000',
      }),
      TranslateModule.forRoot({
        missingTranslationHandler: {
          provide: MissingTranslationHandler,
          useClass: UtishaMissingTranslationHandler,
        },
        useDefaultLang: true,
      }),
    ),
    ...[
      provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json?version=' + version,
      }),
      provideHttpClient(withInterceptorsFromDi()),
      { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
      { provide: BASE_PATH, useValue: environment.apiUrl },
      {
        provide: APP_INITIALIZER,
        useFactory: (initializer: AwsInitializer) => () => initializer.configureAmplify(),
        deps: [AwsInitializer],
        multi: true,
      },
      // Block bootstrap until the user's language JSON is loaded. Without
      // this, components render before `translate.use(...)` resolves its
      // HTTP fetch and the `translate` pipe briefly shows raw keys.
      {
        provide: APP_INITIALIZER,
        useFactory:
          (translate: TranslateService, cookies: CookieService) =>
          async () => {
            const preferred = cookies.getCookieValue('utishaUserLanguage') || 'en';
            translate.setDefaultLang('en');
            try {
              await firstValueFrom(translate.use(preferred));
            } catch {
              // Fall back to English if the preferred locale file can't load.
              await firstValueFrom(translate.use('en'));
            }
          },
        deps: [TranslateService, CookieService],
        multi: true,
      },
      {
        provide: UtishaBackendConfiguration,
        useValue: new UtishaBackendConfiguration({
          basePath: environment.apiUrl,
        }),
      },
      // { provide: APP_INITIALIZER, useFactory: (initializer: PwaInstallService) => () => initializer.init(), deps: [PwaInstallService], multi: true},
      {
        provide: HTTP_INTERCEPTORS,
        useClass: TenantInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: UnauthorizedInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpXsrfInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: LastActiveInterceptor,
        multi: true,
      },
      ZonedDatePipe,
      DatePeriodPipe,
      TimeDiffPipe,
      HtmlDocTypePipe,
      FileSizePipe,
      VideoStatePipe,
      LanguagePipe,
      SafeHtmlPipe,
      ReversePipe,
      SessionProviderFactory,
      StreamSessionProvider,
      // {
      //     provide: FakeProxySessionProvider,
      //     useFactory: FakeProxySessionProviderFactory,
      //     deps: [ZoomSessionProvider]
      // },
      // { provide: SessionProvider, useExisting: ZoomSessionProvider },
      { provide: NZ_I18N, useValue: en_US },
    ].concat(
        ...(environment.production
          ? [
              {
                provide: APP_INITIALIZER,
                useFactory: () => () => {},
                deps: [TraceService],
                multi: true,
              } as any,
              {
                provide: ErrorHandler,
                useValue: createErrorHandler({
                  showDialog: false,
                }),
              },
              {
                provide: TraceService,
                deps: [Router],
              },
            ]
          : []),
      ),
    provideAnimations(),
  ],
})
  .then(() => {})
  .catch((err) => console.error(err));
